import type { BannerType } from '@sunwebgroup/ui-components'
import { useIntl } from '@/intl'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Banner, Button, ListItem, Modal, RadioGroup, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { Icon, IconGlyph, Tag } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'
import { updatePerBookingService } from '../../domain/actions'
import { createAncillariesClickedEvent } from '../../domain/analytics'
import { ServiceType } from '../../domain/types'
import ServiceBox from './ServiceBox'

const Price = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};

  @media ${tk('mq_tablet')} {
    white-space: nowrap;
  }
`
const MoreInformationLink = styled(Typography)`
  text-decoration: underline;
  cursor: pointer;
`
const Table = styled.div`
  position: relative;
  overflow: hidden;
`
const TopInfo = styled(Typography)`
  height: 124px;
  display: flex;
  align-items: end;

  @media ${tk('mq_desktop_sm')} {
    height: 104px;
  }

`
const Features = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: 140px;
  height: 100%;
  background-color: ${tk('color_semantic_bg')};
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  justify-content: end;
  padding-bottom: 100px;

  @media ${tk('mq_desktop_sm')} {
    width: 200px;
  }
  @media ${tk('mq_desktop_md')} {
    width: 140px;
  }
  @media ${tk('mq_desktop_lg')} {
    width: 200px;
  }
`
const FeaturesList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  width: 140px;
  @media ${tk('mq_desktop_sm')} {
    width: 200px;
  }
  @media ${tk('mq_desktop_md')} {
    width: 140px;
  }
  @media ${tk('mq_desktop_lg')} {
    width: 200px;
  }
`
const FeaturesListItem = styled.li`
  padding: 0 ${tk('spacing_xs')} ;
  min-height: 80px;
  color: ${tk('color_semantic_text')};
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:nth-child(even) {
    background-color: ${tk('color_semantic_bg_subdued')};
  }
  ${Icon}{
    display: none;
    @media ${tk('mq_desktop_sm')} {
      display: block;
    }
    @media ${tk('mq_desktop_md')} {
      display: none;
    }
    @media ${tk('mq_desktop_lg')} {
      display: block;
    }
  }
`
const ItemDescription = styled(Typography)`
  word-break: break-word;
`
const ColumnsWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`
const Columns = styled.ul`
  margin-top: ${tk('spacing_none')};
  padding: ${tk('spacing_none')};
  margin-left: 140px;
  display: flex;
  list-style: none;
  width: auto;
  margin-bottom: ${tk('spacing_none')};
  @media ${tk('mq_desktop_sm')} {
    margin-left: 200px;
  }
  @media ${tk('mq_desktop_md')} {
    margin-left: 140px;
  }
  @media ${tk('mq_desktop_lg')} {
    margin-left: 200px;
  }
`
const OptionColumn = styled.li<{ $selected?: boolean }>`
  position: relative;
  float: left;
  width: 120px;
  text-align: center;
  border: 2px solid ${tk('color_semantic_border')};
  border-radius: ${tk('radius_large')} ;
  box-shadow: ${tk('shadow_md_bottom')};
  margin-left: ${tk('spacing_2xs')};
  margin-right: ${tk('spacing_2xs')};
  flex-grow: 1;
  flex-basis: 0;
  padding-top: ${tk('spacing_xl')};
  ${({ $selected }) => $selected && modSelected()}

  ${Button} {
    padding-left: ${tk('spacing_xs')};
    padding-right: ${tk('spacing_xs')};
  }
  @media ${tk('mq_tablet')} {
    padding-top: ${tk('spacing_lg')};
  }
  @media ${tk('mq_desktop_sm')} {
    ${SpaceBetween} {
      padding: ${tk('spacing_sm')};
    }
  }
`
const Badge = styled(Tag)`
  ${Tag.modSize('M')}
  margin-bottom: ${tk('spacing_xs')};
  border-bottom-left-radius: ${tk('radius_none')};
  border-bottom-right-radius: ${tk('radius_none')};
  border-top-right-radius: 6px;
  border-top-left-radius: 6px;
  width: 100%;
  word-break: break-word;
  position: absolute;
  top: 0;
  left: 0;
`
function modSelected() {
  return css`
  border-color: ${tk('color_semantic_border_success')};
  ${Badge} {
    border-top-left-radius: ${tk('radius_medium')};
    border-top-right-radius: ${tk('radius_medium')};
  }
`
}
const OptionColumnHeading = styled(SpaceBetween)`
  height: 80px;
  padding: ${tk('spacing_xs')};

  ${SpaceBetween} {
    padding: ${tk('spacing_none')};
  }
`
const OptionFeaturesList = styled.ul`
  padding: ${tk('spacing_none')};
`
const OptionColumnFooter = styled(SpaceBetween)`
  height: 100px;
`
const OptionFeaturesListItem = styled.li`
  min-height: 80px;
  color: ${tk('color_semantic_text')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &:nth-child(even) {
    background-color: ${tk('color_semantic_bg_subdued')};
  }
  ${Typography} {
    display: flex;
    flex-direction: column;
  }
`
const CrossIcon = styled(Icon)`
  color: ${tk('color_semantic_text_error')};
`
const Percentage = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`
const Texts = styled(SpaceBetween)`
  flex: 1;
`
const SelectedText = styled(Typography)`
  color: ${tk('color_palette_green_500')};
  font-weight: ${tk('font_weight_bold')};
  padding: ${tk('spacing_sm')};
`

export function CancellationInsuranceService() {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.CancellationInsurance)
  const { sendEvent } = useAnalytics()

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))

      const optionClicked = service.options.find(option => option.value === value)
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.percentage?.toString() ?? '',
          type: optionClicked?.label || '',
          description: 'select ancillary with comparison table',
          element: 'button',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  useSendAncillaryAvailable(service, 'selectCancellationInsurance')

  if (!service)
    return null

  const { options, banners, selectedValue } = service

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_description' })}
      glyph={getIconForService(ServiceType.CancellationInsurance)}
      modalTitle={intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_modal_title' })}
      modalDescription={intl.formatMessage({
        id: 'checkout_steps_ancillaries_cancellation_insurance_modal_description',
      })}
    >
      {service.features && service.features.length > 0 && (
        <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
          <Table>
            <Features>
              <TopInfo $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_top_info' })}</TopInfo>
              <FeaturesList>
                {service.features?.map(({ code, icon, description, extraContent }) => {
                  return (
                    <FeaturesListItemView key={code} code={code} icon={icon} description={description} extraContent={extraContent ?? {}} />
                  )
                })}
              </FeaturesList>
            </Features>
            <ColumnsWrapper>
              <Columns>
                {options.map(({ label, badge, percentage, percentageText, value, features: optionFeatures, modalInformation }) => {
                  const isSelected = value === selectedValue
                  return (
                    <OptionColumn key={value} $selected={isSelected ? true : undefined}>
                      <OptionColumnHeading direction="y" spacing={tk('spacing_2xs')}>
                        <Typography $variant="label">{label}</Typography>
                        {percentage && (
                          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                            {percentage && <Percentage $variant="label">{percentage}</Percentage>}
                            {percentageText && <Percentage $variant="body-sm">{percentageText}</Percentage>}
                          </SpaceBetween>
                        )}
                      </OptionColumnHeading>
                      <OptionFeaturesList>
                        {optionFeatures?.map((featureLabel, index) => (
                          <OptionFeaturesListItem key={index}>
                            {featureLabel && (
                              <Typography $variant="body-sm" dangerouslySetInnerHTML={{ __html: featureLabel }} />
                            )}
                            {!featureLabel && <CrossIcon glyph="Dismiss" size={24} />}
                          </OptionFeaturesListItem>
                        ))}
                      </OptionFeaturesList>
                      <OptionColumnFooter direction="y" spacing={tk('spacing_xs')} padding={tk('spacing_2xs')}>
                        {!isSelected && (
                          <Button $hierarchy="primary" $withoutColor onClick={() => handleValueChange(value)}>
                            {intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_select_button' })}
                          </Button>
                        )}
                        {isSelected && <SelectedText $variant="label">{intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_selected_text' })}</SelectedText>}
                        { modalInformation?.title && modalInformation?.body && <OptionColumnExtraInformation title={modalInformation?.title} body={modalInformation?.body} />}
                      </OptionColumnFooter>
                      {badge && <Badge variant="success-tinted">{badge}</Badge>}
                    </OptionColumn>
                  )
                })}
              </Columns>
            </ColumnsWrapper>
          </Table>
          {banners.map(({ title, text, type = 'info' }, index) => (
            <Banner
              key={`banner-${index}`}
              $type={type as BannerType}
            >
              <Texts direction="y">
                {title && <Typography $variant="label">{title}</Typography>}
                {text && <Typography dangerouslySetInnerHTML={{ __html: text }} />}
              </Texts>
            </Banner>
          ))}
        </SpaceBetween>
      )}
      {!service.features && (
        <CancellationInsuranceServicePerBooking />
      )}
    </ServiceBox>
  )
}

function OptionColumnExtraInformation({ title, body }: { title: string, body: string }) {
  const [showModal, setShowModal] = React.useState(false)
  const intl = useIntl()
  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }

  if (!title || !body)
    return null
  return (
    <>
      <MoreInformationLink $variant="body-sm" onClick={openModal}>
        {intl.formatMessage({ id: 'checkout_steps_ancillaries_cancellation_insurance_meer_info' })}
      </MoreInformationLink>
      <Modal.Root open={showModal} onClose={closeModal}>
        <Modal.Content $size="md">
          <Modal.Header>
            <Modal.HeaderTitle text={title} />
            <Modal.HeaderCloseButton />
          </Modal.Header>
          <Modal.Body>
            <SpaceBetween
              direction="y"
              justifyContent="space-between"
              spacing={tk('spacing_xl')}
              padding={tk('spacing_xl')}
            >
              <Typography $variant="body-lg" dangerouslySetInnerHTML={{ __html: body }} />
            </SpaceBetween>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  )
}

function FeaturesListItemView({ code, icon, description, extraContent }: { code: string, icon: string | null, description: string, extraContent: Record<string, string> }) {
  const [showModal, setShowModal] = React.useState(false)

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = () => {
    setShowModal(true)
  }
  return (
    <FeaturesListItem key={code}>
      <SpaceBetween direction="x" spacing={tk('spacing_xs')} alignItems="start" justifyContent="start">
        {icon && <Icon glyph={icon as IconGlyph} size={24} />}
        <span>
          <ItemDescription $variant="body-sm">
            {description}
            {' '}
          </ItemDescription>
          {extraContent && (
            <MoreInformationLink $variant="body-sm" onClick={openModal}>
              {extraContent.linkText}
            </MoreInformationLink>
          )}
        </span>
      </SpaceBetween>
      {extraContent && (
        <Modal.Root open={showModal} onClose={closeModal}>
          <Modal.Content $size="md">
            <Modal.Header>
              <Modal.HeaderTitle text={extraContent.title} />
              <Modal.HeaderCloseButton />
            </Modal.Header>
            <Modal.Body>
              <SpaceBetween
                direction="y"
                justifyContent="space-between"
                spacing={tk('spacing_xl')}
                padding={tk('spacing_xl')}
              >
                <Typography $variant="body-lg" dangerouslySetInnerHTML={{ __html: extraContent.body }} />
              </SpaceBetween>
            </Modal.Body>
          </Modal.Content>
        </Modal.Root>
      )}
    </FeaturesListItem>
  )
}

function CancellationInsuranceServicePerBooking() {
  const { service, setServiceValue } = useService(ServiceType.CancellationInsurancePerBooking)

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service)
        return
      setServiceValue(updatePerBookingService({ value })(service))
    },
    [setServiceValue, service],
  )

  if (!service)
    return null

  const { options, selectedValue } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <RadioGroup.Root value={selectedValue ?? null} onValueChange={handleValueChange}>
        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
          {options.map(({ label, badge, percentage, value }) => (
            <ListItem.Root key={value}>
              <ListItem.Control>
                <RadioGroup.Item value={value} />
              </ListItem.Control>
              <ListItem.Content>
                {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                <ListItem.Label>{label}</ListItem.Label>
              </ListItem.Content>
              <ListItem.Trailing>
                <Price as="span" $variant="label">
                  {percentage}
                </Price>
              </ListItem.Trailing>
            </ListItem.Root>
          ))}
        </SpaceBetween>
      </RadioGroup.Root>
    </SpaceBetween>
  )
}
